/**
 * 🌿 OLIVE THEME
 *
 * Оливковая тема с естественными землистыми цветами.
 * Полностью самодостаточна, содержит все цвета напрямую.
 *
 * Архитектура: все цвета определены непосредственно в теме,
 * с оливковой палитрой и адаптированными текстами.
 */

import type { ThemeTokens } from '../semantic';
import { createThemeWithDerivations } from '../color-derivations';

const baseOliveTheme = {
  // ============================================
  // 📐 SURFACES (поверхности)
  // ============================================
  bg: '#F0EAD6',                          // Дальний слой, темнее (светло-оливковый)
  surface: '#F5F5DC',                     // Средний слой, светлее (кремовый)
  card: '#FAF9F0',                        // Кремовый для карточек
  elevated: '#FFFEF8',                    // Теплый белый для приподнятых (в палитре)

  // ============================================
  // 📝 TEXT (цвета текста для оливковой темы)
  // ============================================
  text: '#2F4F2F',                        // Темно-оливковый основной текст
  textMuted: '#556B2F',                   // Оливково-зеленый вторичный текст
  textTertiary: '#667179',                // Третичный текст (синхронизирован с light)
  textPlaceholder: '#868A92',             // Плейсхолдеры (синхронизирован с light)

  // ============================================
  // 🟡 PRIMARY (основной акцент)
  // ============================================
  primary: '#556B2F',                     // Оливковый акцент
  onPrimary: '#F5F5DC',                    // Кремовый текст на оливковом
  link: '#3D5228',                        // Темно-оливковый для ссылок/иконок на кремовом
  onLightSurface: '#1B1B1B',              // Темный текст на светлых плашках (статусы, карточки)

  // ============================================
  // 📊 BORDERS (границы)
  // ============================================
  border: '#E3D6BF',                      // Мягкие светло-коричневые границы
  borderStrong: '#D1C3AA',                // Средние оливковые границы

  // ============================================
  // ✅ SEMANTIC COLORS (семантические цвета)
  // ============================================
  success: '#4E8A67',
  onSuccess: '#0B0B0B',                   // Контрастный черный для WCAG AA
  warning: '#B9823A',
  onWarning: '#0B0B0B',                   // Контрастный черный для WCAG AA
  error: '#C85C5C',
  onError: '#0B0B0B',                     // Контрастный черный для WCAG AA
  info: '#4E7FA8',
  onInfo: '#0B0B0B',                      // Контрастный черный для WCAG AA

  // ============================================
  // 🎯 INTERACTIVE (интерактивные состояния)
  // ============================================
  focus: 'rgba(85, 107, 47, 0.22)',        // Оливковое кольцо фокуса
  disabled: '#898D91',                     // Отключенный текст (синхронизирован с light)

  // ============================================
  // 🌑 SCRIM (затемнение)
  // ============================================
  scrim: 'rgba(0, 0, 0, 0.05)',             // Заблюренный чёрный 5%

  // ============================================
  // 📜 SCROLLBAR (полоса прокрутки графика аналитики)
  // ============================================
  scrollbarTrack: '#F5F5DC',   // surface
  scrollbarThumb: '#E3D6BF',   // border
  scrollbarThumbHover: '#556B2F', // textMuted

  // ============================================
  // 📅 APPOINTMENT STATUSES (статусы записей)
  // ============================================
  apptScheduledBg: '#BBDEFB', // Blue 100
  apptCompletedBg: '#DCEDC8', // Light Green 100 (теплее)
  apptNoShowBg: '#FFE0B2', // Orange 100 (более оранжевый)
  apptCancelledBg: '#FFCDD2', // Red 100
  apptMovedBg: '#E1BEE7', // Purple 100
  apptBreakBg: '#CFD8DC', // Blue Grey 100
};

export const oliveTheme: ThemeTokens = createThemeWithDerivations(baseOliveTheme, 'olive');